/**
 * Pure helpers that build fact suggestions from three sources — `rule` (client-side
 * phone/email/pet), `stored` (backend pending values), `llm` (/extract-facts) — skipping
 * facts already on file or accepted/rejected.
 */

import type { ILead } from 'src/services/api';
import type { IMessage } from 'src/services/api/messages/types';
import { EMessageDirection } from 'src/services/api/messages/types';

import { extractPhone, extractEmail, extractHasPet, extractPetType } from 'src/services/lead-facts/extractors';
import { computeFactContext } from 'src/services/lead-facts/triggers';
import {
    FACT_LABELS,
    LLM_FIELDS,
    type IFactSuggestion,
    type SpeakerMessage,
} from 'src/services/lead-facts/types';

type SmartFields = Record<string, string | Record<string, string>>;

const TERMINAL_STATUSES = ['accepted', 'rejected'];

function statusOf(smart: SmartFields, key: string): string | undefined {
    const value = smart[`${key}_suggestion_status`];
    return typeof value === 'string' ? value : undefined;
}

function isTerminal(status: string | undefined): boolean {
    return status !== undefined && TERMINAL_STATUSES.includes(status);
}

/** Normalize a fact value for equality checks (case- and space-insensitive). */
function normalizeFactValue(value: string): string {
    return value.trim().toLowerCase();
}

/** Income is coupled: a status on either side governs both. */
function effectiveStatus(smart: SmartFields, key: string): string | undefined {
    const own = statusOf(smart, key);
    if (own !== undefined) {
        return own;
    }
    if (key === 'monthly_income') {
        return statusOf(smart, 'annual_income');
    }
    if (key === 'annual_income') {
        return statusOf(smart, 'monthly_income');
    }
    return undefined;
}

/** Whether `candidate` differs from the value we last suggested for `key`. */
function differsFromLastSuggested(smart: SmartFields, key: string, candidate: string | undefined): boolean {
    if (candidate === undefined) {
        return false;
    }
    const suggested = smart[`${key}_suggested_value`];
    if (typeof suggested !== 'string') {
        return false;
    }
    return normalizeFactValue(suggested) !== normalizeFactValue(candidate);
}

/**
 * Whether a fact should not be suggested: already on file, already accepted, or
 * already rejected — except a rejected fact may be re-suggested when `candidate`
 * differs from the value we last suggested (and were rejected for).
 */
export function shouldSkip(key: string, lead: ILead, candidate?: string): boolean {
    const smart = (lead.smart_fields_data ?? {}) as SmartFields;
    if (key === 'phone_number' && Boolean(lead.phone_number)) {
        return true;
    }
    if (key === 'email' && Boolean(lead.email)) {
        return true;
    }
    /* has_pet is derived from pet_type. */
    if (key === 'has_pet' && isTerminal(statusOf(smart, 'pet_type'))) {
        return true;
    }
    const status = effectiveStatus(smart, key);
    if (status === 'accepted') {
        return true;
    }
    if (status === 'rejected') {
        return !differsFromLastSuggested(smart, key, candidate);
    }
    return false;
}

/** Reduce the conversation to ordered lead/user messages. */
export function toSpeakerMessages(messages: IMessage[]): SpeakerMessage[] {
    return [...messages]
        .sort((a, b) => new Date(a.inserted_at).getTime() - new Date(b.inserted_at).getTime())
        .map((m) => ({
            direction: m.direction,
            contents: m.contents ?? '',
        }));
}

function lastNonNull<T>(extractor: (text: string) => T | null, texts: string[]): T | null {
    let found: T | null = null;
    for (const text of texts) {
        const value = extractor(text);
        if (value !== null) {
            found = value;
        }
    }
    return found;
}

function suggestion(key: string, value: string, source: IFactSuggestion['source']): IFactSuggestion {
    return { key, value, label: FACT_LABELS[key], source };
}

/** Phone/email/pet detected from the conversation (no LLM). */
export function computeSimpleSuggestions(messages: SpeakerMessage[], lead: ILead): IFactSuggestion[] {
    const leadTexts = messages
        .filter((m) => m.direction === EMessageDirection.IN && m.contents)
        .map((m) => m.contents);
    const out: IFactSuggestion[] = [];

    const phone = lastNonNull(extractPhone, leadTexts);
    if (phone !== null && !shouldSkip('phone_number', lead, phone)) {
        out.push(suggestion('phone_number', phone, 'rule'));
    }
    const email = lastNonNull(extractEmail, leadTexts);
    if (email !== null && !shouldSkip('email', lead, email)) {
        out.push(suggestion('email', email, 'rule'));
    }
    const petType = lastNonNull(extractPetType, leadTexts);
    if (petType !== null && !shouldSkip('pet_type', lead, petType)) {
        out.push(suggestion('pet_type', petType, 'rule'));
    } else if (petType === null && lastNonNull(extractHasPet, leadTexts) === true && !shouldSkip('has_pet', lead, 'yes')) {
        out.push(suggestion('has_pet', 'yes', 'rule'));
    }
    return out;
}

/** Suggestions the backend already extracted and stored as pending. */
export function resurfacePendingSuggestions(lead: ILead): IFactSuggestion[] {
    const smart = (lead.smart_fields_data ?? {}) as SmartFields;
    const out: IFactSuggestion[] = [];
    for (const key of Object.keys(FACT_LABELS)) {
        const value = smart[key];
        if (typeof value === 'string' && statusOf(smart, key) === 'pending' && !shouldSkip(key, lead)) {
            out.push(suggestion(key, value, 'stored'));
        }
    }
    return out;
}

/**
 * Whether the lead still warrants an LLM call: at least one extractable field is not
 * already accepted or awaiting action (pending). A rejected field still warrants a
 * call so a freshly-extracted value can be compared against the rejected one.
 */
function needsLlm(lead: ILead): boolean {
    const smart = (lead.smart_fields_data ?? {}) as SmartFields;
    return LLM_FIELDS.some((field) => {
        const status = effectiveStatus(smart, field);
        return status !== 'accepted' && status !== 'pending';
    });
}

/**
 * The conversation slice to send to POST /extract-facts, plus whether to call at all:
 * we call only when something fact-relevant came up AND the lead still has a field
 * worth extracting.
 */
export function computeLlmRequest(
    messages: SpeakerMessage[],
    lead: ILead
): { shouldCall: boolean; slice: SpeakerMessage[] } {
    if (!needsLlm(lead)) {
        return { shouldCall: false, slice: [] };
    }
    const slice = computeFactContext(messages).map((i) => messages[i]);
    return { shouldCall: slice.length > 0, slice };
}

/** Map a POST /extract-facts response into suggestions for display. */
export function factsToSuggestions(
    facts: Record<string, string | number | boolean | null> | undefined,
    lead: ILead
): IFactSuggestion[] {
    if (!facts) {
        return [];
    }
    const out: IFactSuggestion[] = [];

    /*
     * Income is one logical fact; prefer the cadence the lead stated, monthly first.
     * The LLM no longer derives the other figure, so a non-null value here is one
     * the lead actually gave.
     */
    if (facts.monthly_income != null && !shouldSkip('monthly_income', lead, String(facts.monthly_income))) {
        out.push(suggestion('monthly_income', String(facts.monthly_income), 'llm'));
    } else if (facts.annual_income != null && !shouldSkip('annual_income', lead, String(facts.annual_income))) {
        out.push(suggestion('annual_income', String(facts.annual_income), 'llm'));
    }
    if (facts.household_size != null && !shouldSkip('household_size', lead, String(facts.household_size))) {
        out.push(suggestion('household_size', String(facts.household_size), 'llm'));
    }
    /* voucher_type is the only voucher fact we surface. */
    if (facts.voucher_type != null && !shouldSkip('voucher_type', lead, String(facts.voucher_type))) {
        out.push(suggestion('voucher_type', String(facts.voucher_type), 'llm'));
    }
    return out;
}

/**
 * Income is one logical fact with two values. Show a single suggestion: prefer
 * monthly when both monthly and annual are present, otherwise keep whichever exists.
 */
export function collapseIncome(suggestions: IFactSuggestion[]): IFactSuggestion[] {
    const hasMonthly = suggestions.some((s) => s.key === 'monthly_income');
    return hasMonthly ? suggestions.filter((s) => s.key !== 'annual_income') : suggestions;
}

/** Merge suggestion lists, keeping the first occurrence of each key. */
export function dedupeByKey(suggestions: IFactSuggestion[]): IFactSuggestion[] {
    const seen = new Set<string>();
    const out: IFactSuggestion[] = [];
    for (const s of suggestions) {
        if (!seen.has(s.key)) {
            seen.add(s.key);
            out.push(s);
        }
    }
    return out;
}
